import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth.jsx";
import { toast } from "sonner";
import { Activity, ArrowLeft, CheckCircle2, Sparkles } from "lucide-react";
import analytics from "../lib/analytics";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", hpcsa_number: "", role_type: "doctor" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!form.hpcsa_number.trim() || form.hpcsa_number.trim().length < 3) {
      toast.error("HPCSA / MP / student registration number is required");
      return;
    }
    setLoading(true);
    try {
      const newUser = await signup(form);
      // GA4 conversion: send `sign_up` to GA4 via ONE route only — the direct gtag call below.
      // The dataLayer push uses an INTERNAL event name so it can't trigger a second GA4 sign_up in GTM.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "rapidcare_signup_success",
        method: "email",
        role_type: newUser?.role_type || form.role_type || undefined,
      });
      // Single GA4 delivery route: fires ONCE on genuine new account creation (trial auto-activated),
      // never on plain login/returning users.
      if (typeof window.gtag === "function") {
        window.gtag("event", "sign_up", {
          method: "email",
          role_type: newUser?.role_type || form.role_type || undefined,
        });
      }
      toast.success("Account created — welcome");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid md:grid-cols-2">
      <div className="hidden md:flex relative overflow-hidden bg-[#0B5FFF] text-white items-end p-12">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0B5FFF] via-[#1E3A8A] to-[#0A54E0]" />
        <div className="absolute inset-0 medical-grid opacity-20" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-md bg-white text-[#111827] flex items-center justify-center">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-extrabold">RapidCare SA</div>
              <div className="text-[10px] tracking-widest text-slate-300 uppercase">Clinical Reference</div>
            </div>
          </div>
          <h2 className="font-heading text-3xl font-bold max-w-md leading-tight">
            Join verified South African clinicians on RapidCare SA.
          </h2>
          <ul className="mt-6 space-y-2 text-sm text-white/90">
            {["Your 7-day Premium free trial starts automatically — full access to all 500+ protocols, drugs, calculators & ICD-10", "No credit card required — you're never charged automatically", "Then keep free forever access to 50 key emergency protocols", "HPCSA / MP / student registration number required"].map((x, i) => (
              <li key={i} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {x}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <h1 className="font-heading text-3xl font-bold text-[#111827]">Create your account</h1>
          <p className="text-slate-600 mt-2 text-sm">
            Sign up and your <strong className="text-[#111827]">7-day Premium free trial starts automatically</strong> — full access to everything, no credit card required. When it ends you keep free forever access to 50 emergency protocols.
          </p>
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#0B5FFF]/20 bg-[#EFF4FF]/60 p-3" data-testid="signup-trial-note">
            <Sparkles className="w-4 h-4 text-[#0B5FFF] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-700">
              <strong className="text-[#111827]">Instant 7-day Premium trial</strong> — no payment now, and you're never charged automatically.
            </p>
          </div>
          <Card className="mt-6 border-slate-200">
            <CardContent className="p-6">
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="full_name">Full name</Label>
                  <Input id="full_name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5" data-testid="signup-name" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="signup-email" />
                </div>
                <div>
                  <Label htmlFor="role_type">I am a *</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    {[
                      { k: "doctor", label: "Doctor / Clinician" },
                      { k: "student", label: "Medical student" },
                    ].map((o) => (
                      <button
                        key={o.k}
                        type="button"
                        onClick={() => setForm({ ...form, role_type: o.k })}
                        className={`px-3 py-2 rounded-md text-sm font-medium border ${
                          form.role_type === o.k
                            ? "bg-[#0B5FFF] text-white border-[#0B5FFF]"
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`role-${o.k}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="hpcsa_number">
                    HPCSA / MP / Student registration number *
                  </Label>
                  <Input
                    id="hpcsa_number"
                    required
                    minLength={3}
                    value={form.hpcsa_number}
                    onChange={(e) => setForm({ ...form, hpcsa_number: e.target.value })}
                    placeholder={form.role_type === "student" ? "e.g. student / university no. or MP number" : "e.g. MP0123456 or HPCSA registration no."}
                    className="mt-1.5"
                    data-testid="signup-hpcsa"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Enter your MP / HPCSA / student registration number. Required — RapidCare SA is only for verified South African medical professionals and students.
                  </p>
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="signup-password" />
                  <p className="text-xs text-slate-500 mt-1">At least 6 characters.</p>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#0B5FFF] hover:bg-[#0A54E0] h-11" data-testid="signup-submit">
                  {loading ? "Starting your trial..." : "Start my 7-day free trial"}
                </Button>
                <p className="text-[11px] text-slate-500 text-center mt-2">
                  By signing up you agree to our{" "}
                  <Link to="/disclaimer" className="underline">Medical Disclaimer</Link> and{" "}
                  <Link to="/privacy" className="underline">Privacy Policy</Link>.
                </p>
              </form>
              <p className="text-sm text-slate-600 text-center mt-5">
                Already registered?{" "}
                <Link to="/login" className="text-[#111827] font-medium hover:underline" data-testid="go-login">Sign in</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
